import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Flex, Button, Select, toast } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import {
  ComplianceCaseAction as CaseActions,
  ComplianceCaseStatus as CaseStatusEnum,
  ComplianceCaseSubStatus as CaseSubStatusEnum,
  ComplianceCaseTypeEnum as CaseTypeEnum,
  ComplianceCaseResolution as CaseResolutionEnum,
} from "~/graphql/supergraphEnums";
import { usePerformActionMutation } from "~/graphql/supergraphHooks";
import {
  type ComplianceCaseAction,
  type ComplianceCaseStatus,
  type ComplianceCaseSubStatus,
  type ComplianceCaseTypeEnum as ComplianceCaseTypeEnumRawType,
  type GetComplianceCaseQuery,
} from "~/graphql/supergraphTypes";
import { CreateEscalationsNoteModal } from "~/pages/Compliance/CaseDetailsDrawer/Escalations/CreateEscalationsNoteModal";
import { SPACING } from "~/styles/variables";
import { useComplianceEntityOnboardingEnabled } from "../../../flags";
import {
  ESCALATION_REVAMP_CASE_TYPES,
  isInternalEscalationSource,
} from "../../../utils/constants";
import { escalationTakeoverPaths } from "../../Escalations/EscalationTakeover/paths";
import ProposedEscalationsModal from "../../Escalations/ProposedEscalationsModal";
import { EscalationsMenu } from "../EscalationsMenu";
import {
  getEscalationsMenuFlags,
  shouldRenderEscalationsMenu,
} from "../EscalationsMenu/escalationsMenuUtils";
import { gpCaseTypes } from "../MakerControlsSelectMenu/utils";
import { RelatedCasesPill } from "../RelatedCasesPill";
import { smallMargin } from "../styles_vanilla.css";
import { DISABLED_CONCLUSION_TOOLTIP, WAIVER_REQUIRED_TOOLTIP } from "../Tooltips/constants";
import DisabledConclusionTooltip from "../Tooltips/DisabledConclusionTooltip";
import {
  getConfirmedText,
  getInternalLabel,
  getLabel,
  EscalationType,
  isEscalationType,
} from "./utils";

type Props = {
  caseId: number;
  actions: string[];
  caseStatus: ComplianceCaseStatus | undefined | null;
  caseSubstatus: ComplianceCaseSubStatus | undefined | null;
  caseType: ComplianceCaseTypeEnumRawType;
  onSubmit: (submitAction: () => void, action?: ComplianceCaseAction) => void;
  escalationsCount?: number;
  escalations?: NonNullable<GetComplianceCaseQuery["complianceCase"]>["escalations"];
  profilesForCase?: GetComplianceCaseQuery["complianceCase"] | null | undefined;
  caseResolution?: string | null | undefined;
};

const CheckerControlsSelectMenu = ({
  caseId,
  actions,
  caseStatus,
  caseSubstatus,
  caseType,
  onSubmit,
  escalationsCount,
  escalations,
  profilesForCase,
  caseResolution,
}: Props) => {
  const [selectValue, setSelectValue] = useState("");
  const [isEditEscalationOpen, setIsEditEscalationOpen] = useState(false);
  const [selectedEscalationId, setSelectedEscalationId] = useState<number | null>(null);

  const selectedEscalation = useMemo((): EscalationType | undefined => {
    if (!escalations || selectedEscalationId == null) return undefined;
    const match = escalations.find((e) => e?.id === selectedEscalationId);
    return isEscalationType(match) ? match : undefined;
  }, [escalations, selectedEscalationId]);

  const [performActionMutation, { loading: performActionLoading }] = usePerformActionMutation({
    refetchQueries: [
      "GetComplianceCase",
      "GetComplianceCaseAuditLogs",
      "GetComplianceCasesSlice",
    ],
    onCompleted: async () => {
      toast.success(getConfirmedText(caseSubstatus, selectValue));
      await client.refetchQueries({
        include: "active",
      });
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(e.message);
      } else {
        toast.error("An unknown error occurred");
      }
    },
  });
  const sortedActions = useMemo(() => {
    return actions
      .filter((action) => action !== CaseActions.Unassign && action !== CaseActions.Assign)
      .sort((a, b) =>
        a === CaseActions.ReturnToMaker ? 1 : b === CaseActions.ReturnToMaker ? -1 : 0,
      );
  }, [actions]);

  const onChange = (value: string | number | string[] | number[]) => {
    // value is always a string, TypeScript error with onChange handler in Select
    setSelectValue(value as string);
  };

  const [showEscalationsNoteModal, setShowEscalationsNoteModal] = useState(false);

  const [noteModalAction, setNoteModalAction] = useState<ComplianceCaseAction | null>(null);

  const history = useHistory();
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const isInternalOnboardingCase =
    internalEscalationsEnabled && isInternalEscalationSource(caseType);
  const sourceCaseType = profilesForCase?.sourceCase?.caseType as CaseTypeEnum | undefined;
  const optionLabel = (value: string) =>
    (internalEscalationsEnabled &&
      getInternalLabel(value, caseSubstatus, caseType, sourceCaseType)) ||
    getLabel(value, caseSubstatus, caseStatus);

  const handleEscalationConfirm = () => {
    setShowEscalationsNoteModal(false);
    const actionToSend: ComplianceCaseAction = noteModalAction ?? CaseActions.EscalateV2;
    setNoteModalAction(null);
    onSubmit(() =>
      performActionMutation({
        variables: {
          caseId: caseId,
          action: actionToSend,
        },
      }),
    );
  };

  const onSubmitClick = () => {
    const isGpCase = gpCaseTypes.includes(caseType);
    const isPicrOrRemediationCase =
      caseType === CaseTypeEnum.PaymentInstructionChangeReview ||
      caseType === CaseTypeEnum.Remediation;

    if (selectValue === CaseActions.Escalate) {
      setNoteModalAction(CaseActions.EscalateV2);
      setShowEscalationsNoteModal(true);
      return;
    }

    const shouldShowConfirmModal =
      (isPicrOrRemediationCase || isInternalOnboardingCase) &&
      selectValue === CaseActions.Confirm &&
      caseSubstatus === CaseSubStatusEnum.ProposedEscalation;

    if (shouldShowConfirmModal) {
      setNoteModalAction(CaseActions.Confirm);
      setShowEscalationsNoteModal(true);
      return;
    }

    const isOverridingGpAction =
      isGpCase &&
      selectValue === CaseActions.Confirmed &&
      caseSubstatus === CaseSubStatusEnum.ProposedEscalation;

    const finalAction = isOverridingGpAction ? CaseActions.Escalated : selectValue;

    onSubmit(
      () =>
        performActionMutation({
          variables: {
            caseId: caseId,
            action: finalAction as ComplianceCaseAction,
          },
        }),
      finalAction as ComplianceCaseAction,
    );
  };

  const isDisabled = useMemo(() => {
    // Investor Onboarding case in escalated state
    if (
      (caseType === CaseTypeEnum.InvestorOnboarding || caseType === CaseTypeEnum.Remediation) &&
      caseStatus === CaseStatusEnum.UnderReview &&
      caseSubstatus === CaseSubStatusEnum.Escalated
    ) {
      return true;
    }

    // Escalation case missing waiver document
    const isEscalationCase = caseType === CaseTypeEnum.Escalation;
    const waiverDocMissing = (escalations?.[0]?.waiverDocumentId ?? null) == null;
    const sourceIsIoOrPicr =
      !!sourceCaseType && ESCALATION_REVAMP_CASE_TYPES.includes(sourceCaseType);
    const skipWaiverForInternal =
      internalEscalationsEnabled && isInternalEscalationSource(sourceCaseType);
    if (isEscalationCase && sourceIsIoOrPicr && waiverDocMissing && !skipWaiverForInternal) {
      return true;
    }

    // PICR case with escalated resolution
    if (
      caseType === CaseTypeEnum.PaymentInstructionChangeReview &&
      caseResolution === CaseResolutionEnum.Escalated
    ) {
      return true;
    }

    // Remediation case with escalated resolution (follows PICR logic)
    if (
      caseType === CaseTypeEnum.Remediation &&
      caseResolution === CaseResolutionEnum.Escalated
    ) {
      return true;
    }

    // Entity/client onboarding case with an outstanding internal escalation
    if (isInternalOnboardingCase && caseResolution === CaseResolutionEnum.Escalated) {
      return true;
    }

    return false;
  }, [
    caseType,
    caseStatus,
    caseSubstatus,
    caseResolution,
    sourceCaseType,
    escalations,
    isInternalOnboardingCase,
    internalEscalationsEnabled,
  ]);

  const showDisabledTooltip = useMemo(() => {
    const isIoEscalatedState =
      caseType === CaseTypeEnum.InvestorOnboarding &&
      caseResolution === CaseResolutionEnum.IoUnderReviewEscalated;
    return isIoEscalatedState || isDisabled;
  }, [caseType, caseResolution, isDisabled]);

  const renderConclusionSelect = () => (
    <Select
      placeholder="Select resolver conclusion"
      position="up"
      value={selectValue}
      onChange={onChange}
      css={{ margin: SPACING.SMALL }}
      customLabel={getDisplayText(selectValue)}
      disabled={isDisabled}
    >
      <Select.Section>
        {sortedActions.map((option, index) => (
          <Select.Option value={option} key={index}>
            {optionLabel(option)}
          </Select.Option>
        ))}
      </Select.Section>
    </Select>
  );

  //for particular options, we want to display a different text on UI from what getLabel returns
  const getDisplayText = (value: string) => {
    if (value === CaseActions.Escalate) {
      return "Confirm GP escalation required";
    } else if (value === "" || value === null || value === undefined) return undefined;
    else {
      return optionLabel(value);
    }
  };

  return (
    <Flex style={{ marginRight: `${SPACING.MEDIUM}` }}>
      {isEditEscalationOpen && selectedEscalation && (
        <ProposedEscalationsModal
          show={isEditEscalationOpen}
          onCloseModal={() => setIsEditEscalationOpen(false)}
          caseType={caseType}
          caseStatus={caseStatus ?? CaseStatusEnum.Ready}
          caseId={caseId}
          escalation={selectedEscalation}
          profilesForCase={profilesForCase}
        />
      )}
      <RelatedCasesPill
        currentCaseId={caseId}
        investingProfileId={profilesForCase?.investingProfile?.id}
        caseIsClosed={false}
        className={smallMargin}
      />
      {shouldRenderEscalationsMenu(caseType, escalations, internalEscalationsEnabled) && (
        <div className={smallMargin}>
          <EscalationsMenu
            caseId={caseId}
            escalations={escalations}
            profilesForCase={profilesForCase}
            {...getEscalationsMenuFlags(caseResolution, caseId, profilesForCase)}
            onSelect={(id) => {
              if (internalEscalationsEnabled) {
                history.push(escalationTakeoverPaths.view(caseId, id), {
                  from: history.location.pathname,
                });
                return;
              }
              setSelectedEscalationId(id);
              setIsEditEscalationOpen(true);
            }}
          />
        </div>
      )}
      <CreateEscalationsNoteModal
        show={showEscalationsNoteModal}
        onHide={() => setShowEscalationsNoteModal(false)}
        onContinue={handleEscalationConfirm}
        escalationsCount={escalationsCount}
        escalations={escalations ?? undefined}
        profilesForCase={profilesForCase}
      />
      {showDisabledTooltip ? (
        <DisabledConclusionTooltip
          message={(() => {
            const sourceCaseType = profilesForCase?.sourceCase?.caseType as
              CaseTypeEnum | undefined;
            const isEscalationCase = caseType === CaseTypeEnum.Escalation;
            const waiverDocMissing = (escalations?.[0]?.waiverDocumentId ?? null) == null;
            const sourceIsIoOrPicr =
              !!sourceCaseType && ESCALATION_REVAMP_CASE_TYPES.includes(sourceCaseType);
            const skipWaiverForInternal =
              internalEscalationsEnabled && isInternalEscalationSource(sourceCaseType);

            if (
              isEscalationCase &&
              sourceIsIoOrPicr &&
              waiverDocMissing &&
              !skipWaiverForInternal
            ) {
              return WAIVER_REQUIRED_TOOLTIP;
            }
            return DISABLED_CONCLUSION_TOOLTIP;
          })()}
        >
          <div>{renderConclusionSelect()}</div>
        </DisabledConclusionTooltip>
      ) : (
        renderConclusionSelect()
      )}

      <Button
        css={{ margin: SPACING.SMALL }}
        variant="primary"
        onClick={onSubmitClick}
        isLoading={performActionLoading}
        disabled={isDisabled || selectValue == ""}
      >
        Submit
      </Button>
    </Flex>
  );
};

export default CheckerControlsSelectMenu;
